import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  ShoppingCart,
  People,
  AttachMoney,
  Schedule,
  Assessment,
  Warning,
  Notifications,
  Download,
  Info,
  PointOfSale,
  Login
} from '@mui/icons-material';
import orderService from '../services/orderService';
import userLogService from '../services/userLogService';
import employerService from '../services/employerService';
import webOrderService from '../services/webOrderService';
import './AdminDashboard.css';

const AdminDashboard = ({ onPageChange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('today');
  const [dashboardData, setDashboardData] = useState({
    stats: {},
    recentOrders: [],
    activityLogs: [],
    onlineCashiers: [],
  });
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [ordersResponse, statsResponse, employersResponse, activityResponse, webOrdersResponse] = await Promise.all([
        orderService.getAllOrders({ status: 'completed', per_page: 5 }),
        orderService.getDashboardStats({ range: timeRange }),
        employerService.getAllEmployers(),
        userLogService.getAllLogs({ per_page: 5 }),
        webOrderService.getAllWebOrders({ per_page: 5 })
      ]);

      const onlineCashiers = employersResponse.data?.data?.filter(emp =>
        emp.is_online && emp.user_role === 'Cashier'
      ) || [];

      const regularOrders = ordersResponse.data?.data || [];
      const webOrders = webOrdersResponse.data?.data || [];

      const allRecentOrders = [...regularOrders, ...webOrders]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      const combinedStats = calculateCombinedStats(statsResponse.data, webOrdersResponse.data);

      setDashboardData({
        stats: combinedStats,
        recentOrders: allRecentOrders,
        activityLogs: activityResponse.data?.data || [],
        onlineCashiers: onlineCashiers,
      });
      setLastUpdated(new Date());
      setLoading(false);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data: ' + (error.message || 'Unknown error'));
      setLoading(false);
    }
  };

  const calculateCombinedStats = (regularStats, webOrdersData) => {
    const webOrders = webOrdersData?.data || [];

    const webOrdersTotal = webOrders.length;
    const webOrdersRevenue = webOrders.reduce((total, order) =>
      total + (parseFloat(order.total_amount) || 0), 0
    );

    const totalRevenue = (regularStats?.total_revenue || 0) + webOrdersRevenue;
    const totalOrders = (regularStats?.total_orders || 0) + webOrdersTotal;
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    return {
      total_revenue: totalRevenue,
      total_orders: totalOrders,
      average_order_value: averageOrderValue,
      total_customers: regularStats?.total_customers || 0,
      conversion_rate: regularStats?.conversion_rate || 0,
      products_sold: regularStats?.products_sold || 0,
      refund_rate: regularStats?.refund_rate || 0,
      payment_methods: regularStats?.payment_methods || [],
      web_orders_total: webOrdersTotal,
      web_orders_revenue: webOrdersRevenue,
      regular_orders_total: regularStats?.total_orders || 0,
      regular_orders_revenue: regularStats?.total_revenue || 0
    };
  };

  const formatCurrency = (amount) => {
    return `LKR ${(amount || 0).toLocaleString('en-LK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (number) => {
    return (number || 0).toLocaleString('en-LK');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const isWebOrder = (order) => {
    return order.hasOwnProperty('order_type') && order.order_type === 'web';
  };

  const getOrderSourceChip = (order) => {
    if (isWebOrder(order)) {
      return <Chip label="Web" size="small" color="primary" variant="outlined" />;
    }
    return <Chip label="Store" size="small" color="secondary" variant="outlined" />;
  };

  const handleViewAllOrders = () => {
    if (onPageChange && typeof onPageChange === 'function') {
      onPageChange('AllOrders');
    }
  };

  const handleViewAllCashiers = () => {
    if (onPageChange && typeof onPageChange === 'function') {
      onPageChange('employer-management');
    }
  };

  const handleViewAllActivity = () => {
    if (onPageChange && typeof onPageChange === 'function') {
      onPageChange('user-log');
    }
  };

  const handlePosCashierLogin = () => {
    window.open('https://cashier.litegrocer.lk', '_blank');
  };

  const handleExportDashboard = () => {
    try {
      const exportData = [
        {
          'Metric': 'Total Revenue',
          'Value': formatCurrency(dashboardData.stats.total_revenue),
          'Category': 'Financial'
        },
        {
          'Metric': 'Total Orders',
          'Value': formatNumber(dashboardData.stats.total_orders),
          'Category': 'Sales'
        },
        {
          'Metric': 'Online Cashiers',
          'Value': dashboardData.onlineCashiers.length,
          'Category': 'Staff'
        },
        {
          'Metric': 'Average Order Value',
          'Value': formatCurrency(dashboardData.stats.average_order_value),
          'Category': 'Financial'
        }
      ];

      // For now, let's just log the data and show a success message
      console.log('Export data:', exportData);
      alert('Export functionality would be implemented here. Data prepared for export.');

      // If you have the excelExport utility, uncomment this:
      /*
      if (exportData.length > 0) {
        const excelExport = require('../../../utils/excelExport');
        excelExport.exportToExcel(exportData, 'Dashboard_Overview');
      } else {
        setError('No data available for export');
      }
      */
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export dashboard data: ' + err.message);
    }
  };

  const renderMetricCard = (title, value, subtitle = '', format = 'currency', color = 'primary', trend = null) => {
    const formattedValue = format === 'currency' ? formatCurrency(value) : formatNumber(value);

    return (
      <Card className="admin-metric-card">
        <CardContent className="admin-metric-content">
          <div className="admin-metric-header">
            <Typography className="admin-metric-title">
              {title}
            </Typography>
            {subtitle && (
              <Tooltip title={subtitle}>
                <Info className="admin-metric-info" />
              </Tooltip>
            )}
          </div>
          <Typography className={`admin-metric-value admin-metric-${color}`}>
            {formattedValue}
          </Typography>
          {trend && (
            <div className={`admin-metric-trend admin-trend-${trend.direction}`}>
              <Typography className="admin-trend-text">
                {trend.value}% {trend.direction === 'up' ? 'increase' : 'decrease'}
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box className="admin-container">
        <Box className="admin-loading-container">
          <CircularProgress size={60} className="admin-loading-spinner" />
          <Typography className="admin-loading-text">
            Loading dashboard data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="admin-container">
      {/* Header */}
      <Card className="admin-header-card">
        <CardContent className="admin-header-content">
          <div className="admin-header-main">
            <div className="admin-header-text">
              <Typography className="admin-header-title">Admin Dashboard</Typography>
              <Typography className="admin-header-subtitle">
                Real-time overview of store operations and performance across all channels
              </Typography>
            </div>
          </div>
          <div className="admin-header-actions">
            <Tooltip title="Refresh Data">
              <IconButton
                onClick={loadDashboardData}
                disabled={loading}
                className="admin-refresh-button"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={<PointOfSale />}
              onClick={handlePosCashierLogin}
              className="admin-pos-button"
            >
              POS Cashier Login
            </Button>
            {/* <Button
              startIcon={<Download />}
              onClick={handleExportDashboard}
              disabled={loading}
              className="admin-export-button"
            >
              Export Report
            </Button> */}
          </div>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card className="admin-controls-card">
        <CardContent className="admin-controls-content">
          <div className="admin-controls-header">
            <Typography className="admin-controls-title">Time Range</Typography>
            {lastUpdated && (
              <Typography className="admin-updated-text">
                Last updated: {lastUpdated.toLocaleString()}
              </Typography>
            )}
          </div>
          <div className="admin-controls-grid">
            <FormControl fullWidth className="admin-select-control">
              <InputLabel>Select Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Select Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="year">This Year</MenuItem>
              </Select>
            </FormControl>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert
          severity="error"
          className="admin-error-alert"
          onClose={() => setError('')}
          action={
            <Button color="inherit" size="small" onClick={loadDashboardData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Key Metrics */}
      <div className="admin-metrics-section">
        <Typography className="admin-section-title">Performance Overview</Typography>
        <div className="admin-metrics-grid">
          {renderMetricCard(
            'Total Revenue',
            dashboardData.stats.total_revenue || 0,
            'Total revenue from all sales channels',
            'currency',
            'primary'
          )}
          {renderMetricCard(
            'Total Orders',
            dashboardData.stats.total_orders || 0,
            'Total number of transactions processed',
            'number',
            'secondary'
          )}
          {renderMetricCard(
            'Customers Served',
            dashboardData.stats.total_customers || 0,
            'Total unique customers served',
            'number',
            'success'
          )}
          {renderMetricCard(
            'Online Cashiers',
            dashboardData.onlineCashiers.length || 0,
            'Currently active cashiers',
            'number',
            'info'
          )}
        </div>

        {/* Revenue Breakdown */}
        <Card className="admin-breakdown-card">
          <CardContent className="admin-breakdown-content">
            <div className="admin-breakdown-header">
              <AttachMoney className="admin-breakdown-icon" />
              <Typography className="admin-breakdown-title">Revenue Breakdown</Typography>
            </div>
            <div className="admin-breakdown-stats">
              <div className="admin-breakdown-item">
                <Typography className="admin-breakdown-label">Store Revenue</Typography>
                <Typography className="admin-breakdown-value admin-store-revenue">
                  {formatCurrency(dashboardData.stats.regular_orders_revenue || 0)}
                </Typography>
                <Typography className="admin-breakdown-percentage">
                  {dashboardData.stats.total_revenue > 0 ?
                    `${((dashboardData.stats.regular_orders_revenue / dashboardData.stats.total_revenue) * 100).toFixed(1)}%` : '0%'
                  }
                </Typography>
              </div>
              <div className="admin-breakdown-item">
                <Typography className="admin-breakdown-label">Web Revenue</Typography>
                <Typography className="admin-breakdown-value admin-web-revenue">
                  {formatCurrency(dashboardData.stats.web_orders_revenue || 0)}
                </Typography>
                <Typography className="admin-breakdown-percentage">
                  {dashboardData.stats.total_revenue > 0 ?
                    `${((dashboardData.stats.web_orders_revenue / dashboardData.stats.total_revenue) * 100).toFixed(1)}%` : '0%'
                  }
                </Typography>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="admin-content-grid">
        {/* Recent Orders */}
        <Card className="admin-content-card">
          <CardContent className="admin-card-content">
            <div className="admin-card-header">
              <div className="admin-card-title-section">
                <ShoppingCart className="admin-card-icon" />
                <Typography className="admin-card-title">Recent Orders</Typography>
              </div>
              <Chip
                label={dashboardData.recentOrders.length}
                className="admin-card-badge"
              />
            </div>
            <div className="admin-card-body">
              {dashboardData.recentOrders.length > 0 ? (
                <div className="admin-orders-list">
                  {dashboardData.recentOrders.map(order => (
                    <div key={order.id} className="admin-order-item">
                      <div className="admin-order-main">
                        <div className="admin-order-info">
                          <Typography className="admin-order-number">
                            {order.order_number}
                          </Typography>
                          {getOrderSourceChip(order)}
                        </div>
                        <Typography className="admin-order-amount">
                          {formatCurrency(order.total_amount)}
                        </Typography>
                      </div>
                      <div className="admin-order-details">
                        <Chip
                          label={order.status}
                          size="small"
                          color={getStatusColor(order.status)}
                          className="admin-order-status"
                        />
                        <Typography className="admin-order-time">
                          {formatDate(order.created_at)}
                        </Typography>
                      </div>
                      {order.cashier && !isWebOrder(order) && (
                        <div className="admin-order-cashier">
                          <Typography className="admin-cashier-text">
                            Cashier: {order.cashier.full_legal_name}
                          </Typography>
                        </div>
                      )}
                      {isWebOrder(order) && order.customer_name && (
                        <div className="admin-order-customer">
                          <Typography className="admin-customer-text">
                            Customer: {order.customer_name}
                          </Typography>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="admin-no-data">
                  <ShoppingCart className="admin-no-data-icon" />
                  <Typography className="admin-no-data-text">
                    No recent orders
                  </Typography>
                </div>
              )}
            </div>
            <div className="admin-card-footer">
              <Button
                onClick={handleViewAllOrders}
                className="admin-view-all-button"
              >
                View All Orders
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="admin-content-card">
          <CardContent className="admin-card-content">
            <div className="admin-card-header">
              <div className="admin-card-title-section">
                <Notifications className="admin-card-icon" />
                <Typography className="admin-card-title">Recent Activity</Typography>
              </div>
              <Chip
                label={dashboardData.activityLogs.length}
                className="admin-card-badge"
              />
            </div>
            <div className="admin-card-body">
              {dashboardData.activityLogs.length > 0 ? (
                <div className="admin-activity-list">
                  {dashboardData.activityLogs.map(log => (
                    <div key={log.id} className="admin-activity-item">
                      <Avatar className="admin-activity-avatar">
                        {log.username?.charAt(0).toUpperCase()}
                      </Avatar>
                      <div className="admin-activity-details">
                        <Typography className="admin-activity-description">
                          {log.description}
                        </Typography>
                        <div className="admin-activity-meta">
                          <Typography className="admin-activity-user">
                            {log.username}
                          </Typography>
                          <Typography className="admin-activity-time">
                            {formatDate(log.created_at)}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="admin-no-data">
                  <Notifications className="admin-no-data-icon" />
                  <Typography className="admin-no-data-text">
                    No recent activity
                  </Typography>
                </div>
              )}
            </div>
            <div className="admin-card-footer">
              <Button
                onClick={handleViewAllActivity}
                className="admin-view-all-button"
              >
                View All Activity
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Online Cashiers */}
        <Card className="admin-content-card">
          <CardContent className="admin-card-content">
            <div className="admin-card-header">
              <div className="admin-card-title-section">
                <People className="admin-card-icon" />
                <Typography className="admin-card-title">Online Cashiers</Typography>
              </div>
              <Chip
                label={dashboardData.onlineCashiers.length}
                className="admin-card-badge admin-online-badge"
              />
            </div>
            <div className="admin-card-body">
              {dashboardData.onlineCashiers.length > 0 ? (
                <div className="admin-cashiers-list">
                  {dashboardData.onlineCashiers.map(cashier => (
                    <div key={cashier.id} className="admin-cashier-item">
                      <Avatar className="admin-cashier-avatar admin-online-avatar">
                        {cashier.full_legal_name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <div className="admin-cashier-details">
                        <Typography className="admin-cashier-name">
                          {cashier.full_legal_name}
                        </Typography>
                        <Typography className="admin-cashier-id">
                          ID: {cashier.employee_id}
                        </Typography>
                        <div className="admin-cashier-status">
                          <div className="admin-status-dot admin-online-dot" />
                          <Typography className="admin-status-text">
                            Online
                          </Typography>
                        </div>
                      </div>
                      <Tooltip title="Send Message">
                        <IconButton className="admin-cashier-action">
                          💬
                        </IconButton>
                      </Tooltip>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="admin-no-data">
                  <People className="admin-no-data-icon" />
                  <Typography className="admin-no-data-text">
                    No cashiers online
                  </Typography>
                </div>
              )}
            </div>
            <div className="admin-card-footer">
              <Button
                onClick={handleViewAllCashiers}
                className="admin-view-all-button"
              >
                Manage Cashiers
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Performance Metrics */}
        <Card className="admin-content-card">
          <CardContent className="admin-card-content">
            <div className="admin-card-header">
              <div className="admin-card-title-section">
                <Assessment className="admin-card-icon" />
                <Typography className="admin-card-title">Performance Metrics</Typography>
              </div>
            </div>
            <div className="admin-card-body">
              <div className="admin-performance-grid">
                <div className="admin-performance-item">
                  <Typography className="admin-performance-value">
                    {formatCurrency(dashboardData.stats.average_order_value || 0)}
                  </Typography>
                  <Typography className="admin-performance-label">
                    Average Order Value
                  </Typography>
                </div>
                <div className="admin-performance-item">
                  <Typography className="admin-performance-value">
                    {dashboardData.stats.conversion_rate || 0}%
                  </Typography>
                  <Typography className="admin-performance-label">
                    Conversion Rate
                  </Typography>
                </div>
                <div className="admin-performance-item">
                  <Typography className="admin-performance-value">
                    {formatNumber(dashboardData.stats.products_sold || 0)}
                  </Typography>
                  <Typography className="admin-performance-label">
                    Products Sold
                  </Typography>
                </div>
                <div className="admin-performance-item">
                  <Typography className="admin-performance-value">
                    {dashboardData.stats.refund_rate || 0}%
                  </Typography>
                  <Typography className="admin-performance-label">
                    Refund Rate
                  </Typography>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="admin-content-card">
          <CardContent className="admin-card-content">
            <div className="admin-card-header">
              <div className="admin-card-title-section">
                <Schedule className="admin-card-icon" />
                <Typography className="admin-card-title">Quick Actions</Typography>
              </div>
            </div>
            <div className="admin-card-body">
              <div className="admin-actions-grid">
                <Button className="admin-quick-action" startIcon={<PointOfSale />} onClick={handlePosCashierLogin}>
                  POS Login
                </Button>
                {/* <Button className="admin-quick-action" startIcon={<Assessment />}>
                  View Reports
                </Button>
                <Button className="admin-quick-action" startIcon={<People />}>
                  Manage Users
                </Button>
                <Button className="admin-quick-action" startIcon={<ShoppingCart />}>
                  Inventory
                </Button>
                <Button className="admin-quick-action" startIcon={<Schedule />}>
                  Settings
                </Button>
                <Button className="admin-quick-action" startIcon={<Refresh />}>
                  Sync Data
                </Button> */}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Status */}
        <Card className="admin-content-card">
          <CardContent className="admin-card-content">
            <div className="admin-card-header">
              <div className="admin-card-title-section">
                <Warning className="admin-card-icon" />
                <Typography className="admin-card-title">System Status</Typography>
              </div>
            </div>
            <div className="admin-card-body">
              <div className="admin-status-list">
                <div className="admin-status-item admin-status-operational">
                  <div className="admin-status-indicator" />
                  <Typography className="admin-status-service">POS System</Typography>
                  <Typography className="admin-status-state">Operational</Typography>
                </div>
                <div className="admin-status-item admin-status-operational">
                  <div className="admin-status-indicator" />
                  <Typography className="admin-status-service">Ecommerce</Typography>
                  <Typography className="admin-status-state">Operational</Typography>
                </div>
                <div className="admin-status-item admin-status-operational">
                  <div className="admin-status-indicator" />
                  <Typography className="admin-status-service">Database</Typography>
                  <Typography className="admin-status-state">Operational</Typography>
                </div>
                <div className="admin-status-item admin-status-operational">
                  <div className="admin-status-indicator" />
                  <Typography className="admin-status-service">Payment Gateway</Typography>
                  <Typography className="admin-status-state">Not Operational</Typography>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Box>
  );
};

export default AdminDashboard;